import { getAllObjectPaths } from '@eigtech/function-utils'
import { Corn } from '@eigtech/shared-corn'
import { useGetAssignmentsForAssignables } from '@eigtech/ui-shared-assignments'
import { getContactRoleLabel } from '@eigtech/ui-shared-contacts'
import { useDatesContext } from '@eigtech/ui-shared-dates'
import {
  InputField,
  TextareaField,
  useFormContext,
  useWatch,
} from '@eigtech/ui-shared-forms/shadcn'
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  BeautifulMentionComponentProps,
  BeautifulMentionsMenuItemProps,
  InlineMention,
  LexicalEditor,
  Mention,
  MentionsInputField,
  MentionsMenuItem,
  useBeautifulMentions,
} from '@eigtech/ui-shared-mentions'
import { TaggableContact, useGetTaggableContacts } from '@eigtech/ui-shared-notes'
import { Button } from '@eigtech/ui/components/button'
import { ButtonGroup } from '@eigtech/ui/components/button-group'
import { Stack } from '@eigtech/ui/components/stack'
import { Tag } from '@eigtech/ui/components/tag'
import { Tooltip, TooltipContent, TooltipTrigger } from '@eigtech/ui/components/tooltip'
import { Wrap, WrapItem } from '@eigtech/ui/components/wrap'
import { get, startCase } from 'lodash-es'
import { useCallback, useEffect, useRef } from 'react'
import { z } from 'zod'
import { NoteFieldsType, isNoteMention } from './NoteFieldsSchema'

export type NoteFieldsProps = {
  entityCorn: Corn | Corn[]
  templateParams?: Record<string, unknown>
}

export function NoteFields({ entityCorn, templateParams }: NoteFieldsProps) {
  const { preferredFormatDateFunction } = useDatesContext()

  const { getValues, setValue, control } = useFormContext<NoteFieldsType>()

  const taggableContacts = useGetTaggableContacts({ entityCorn })

  const queries = useGetAssignmentsForAssignables([entityCorn].flat())

  const taggableLookup = taggableContacts.map(({ roles, ...taggableContact }) => {
    const contactAssignments = queries
      .flatMap(({ data }) => data ?? [])
      .filter(
        (assignment) =>
          assignment.assignee.type === 'contact' &&
          assignment.assignee.assigneeId === taggableContact.id
      )
      .map((assignment) => startCase(assignment.assignable.assigneeRelationship))
      .join('|')

    return {
      ...taggableContact,
      roles: JSON.stringify(roles),
      lookup: `${roles.join('|')}|${contactAssignments}|${taggableContact.value}`.toLowerCase(),
    }
  })

  const paramsLookup = getAllObjectPaths(templateParams ?? {})
    .filter((path) => !!get(templateParams ?? {}, path))
    .map((path) => {
      const baseValue = String(get(templateParams ?? {}, path))
      const value = z.string().date().safeParse(baseValue).success
        ? preferredFormatDateFunction({ dateTime: baseValue, property: path })
        : baseValue

      return {
        value,
        path,
        param: value,
      }
    })

  const enableMentions = !!(taggableContacts?.length || paramsLookup.length)

  const editorRef = useRef<LexicalEditor>(null)

  const template = useWatch({ control, name: 'template' })

  // Programmatically update the mentions input when template selection changes
  // Only update if template has been selected and template has a text template
  useEffect(() => {
    // Don't need to do programatic update if mentions are not enabled
    if (!enableMentions) return

    if (!template) return
    if (!('textTemplate' in template)) return
    if (!template.textTemplate) return

    editorRef.current?.update(() => {
      const root = $getRoot()
      root.clear()

      const paragraphNode = $createParagraphNode()

      // Intentionally using the actual `text` value of the form here
      // This will have the compiled template params
      const textNode = $createTextNode(getValues().text)

      paragraphNode.append(textNode)
      root.append(paragraphNode)
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template])

  const getTaggable = async (trigger: string, query?: string | null) => {
    switch (trigger) {
      case '#':
        return !query
          ? paramsLookup
          : paramsLookup.filter((t) =>
              t.path.toLowerCase().trim().includes(query.toLowerCase().trim())
            )

      default:
        return !query
          ? taggableLookup
          : taggableLookup.filter((t) => t.lookup.includes(query.toLowerCase()))
    }
  }

  const onMentionsChange = useCallback(
    (mentions: Mention[]) => {
      setValue('mentions', mentions.filter(isNoteMention))
    },
    [setValue]
  )

  return (
    <>
      <InputField label="Note Title" name="title" />

      {enableMentions ? (
        <div className="flex-1 [&_.chakra-form-control]:flex [&_.chakra-form-control]:h-full [&_.chakra-form-control]:flex-col [&_.chakra-form-control_div:has(.mentions-input)]:h-full [&_.chakra-form-control_div:has(.mentions-input)]:flex-1 [&_.chakra-form-control_div:has(.mentions-input)_.mentions-input]:h-full">
          <MentionsInputField
            ref={editorRef}
            MentionComponent={CustomMentionComponent}
            className="min-h-52 shrink-0 grow"
            label="Note Text"
            menuItemComponent={CustomMentionsMenuItem}
            name="text"
            triggers={triggers}
            onMentionsChange={onMentionsChange}
            onSearch={getTaggable}
          >
            <MentionsActions showInsertParameter={!!paramsLookup.length} />
          </MentionsInputField>
        </div>
      ) : (
        <TextareaField label="Note Text" name="text" rows={12} />
      )}
    </>
  )
}

function CustomMentionsMenuItem({
  ref,
  item,
  ...props
}: BeautifulMentionsMenuItemProps & { ref?: React.Ref<HTMLLIElement> }) {
  const roles =
    typeof item.data?.roles === 'string' &&
    (JSON.parse(item.data.roles) as TaggableContact['roles'])

  return (
    <MentionsMenuItem ref={ref} item={item} {...props} minW="sm">
      {!!item.data && 'path' in item.data ? (
        <Stack className="gap-0.5">
          <span>{item.data.path}</span>
          <span className="text-sm text-gray-500">{item.data.param}</span>
        </Stack>
      ) : (
        <Stack className="gap-0.5">
          <span>{item.data?.name ?? item.displayValue}</span>
          {Array.isArray(roles) && (
            <Wrap className="gap-1">
              {roles.map(getContactRoleLabel).map((role) => (
                <WrapItem key={role}>
                  <Tag size="sm">{role}</Tag>
                </WrapItem>
              ))}
            </Wrap>
          )}
        </Stack>
      )}
    </MentionsMenuItem>
  )
}

function MentionsActions({ showInsertParameter }: { showInsertParameter: boolean }) {
  const { openMentionMenu, hasMentions } = useBeautifulMentions()

  if (!hasMentions) return null

  return (
    <ButtonGroup className="mb-2 justify-end gap-1" size="xs" variant="outline">
      <Button onClick={() => openMentionMenu({ trigger: '@' })}>Insert Mention</Button>
      {!!showInsertParameter && (
        <Button onClick={() => openMentionMenu({ trigger: '#' })}>Insert Parameter</Button>
      )}
    </ButtonGroup>
  )
}

function CustomMentionComponent({
  trigger: __trigger,
  value: __value,
  data,
  children,
  ...props
}: BeautifulMentionComponentProps) {
  const name = (data as { name: string } | undefined)?.name
  const path = (data as { path: string } | undefined)?.path

  return (
    <Tooltip disabled={!path}>
      <TooltipTrigger>
        <InlineMention {...props}>{name ? `@${name}` : children}</InlineMention>
      </TooltipTrigger>

      <TooltipContent>
        <span>{path}</span>
      </TooltipContent>
    </Tooltip>
  )
}

const triggers = ['@', '#']
